<template src="./category.html"></template>

<script>
import axios from 'axios'
  import save from './modal/save'
  import { store } from '../../store/store'
  import SettingSidebar from '../common/settingsidebar/settingsidebar'
  import auth from '../../helpers/auth'

  export default {
    name: 'category',
    components: {
      'app-save-category-modal': save,
      'app-setting-sidebar': SettingSidebar
    },
    data: function () {
      return {
        category: {},
        categories: [],
        flatCategories: [],
        modalVisible: false,
        loading: false,
        searchText: '',
        filterType: '',
        draggedIndex: null,
        dragOverIndex: null,
        categoryDragMoved: false,
        savingOrder: false
      }
    },
    computed: {
      canReorderCategories: function () {
        return this.$store.getters['user/isCatalogEditor'] && !this.searchText && !this.filterType
      },
      filteredCategories: function () {
        if (!this.categories || !this.flatCategories) {
          return [];
        }

        if (!this.searchText && !this.filterType) {
          return this.categories;
        }

        let filtered = this.flatCategories;

        if (this.searchText) {
          const searchLower = this.searchText.toLowerCase();
          filtered = filtered.filter(cat =>
            cat.name.toLowerCase().includes(searchLower) ||
            (cat.description && cat.description.toLowerCase().includes(searchLower))
          );
        }

        if (this.filterType) {
          filtered = filtered.filter(cat => cat.type === this.filterType);
        }

        return this.buildHierarchy(filtered);
      },
      displayRows: function () {
        const rows = [];
        const walk = (items, depth, parentName) => {
          (items || []).forEach((cat) => {
            rows.push({
              category: cat,
              depth: depth,
              parentName: parentName,
              parentId: this.normalizeParentId(cat.parent_id)
            });
            if (cat.children && cat.children.length) {
              walk(cat.children, depth + 1, cat.name);
            }
          });
        };
        walk(this.filteredCategories, 0, null);
        return rows;
      }
    },
    methods: {
      normalizeParentId: function (parentId) {
        return parentId === 0 || parentId === null || parentId === undefined ? null : parentId;
      },
      compareCategories: function (a, b) {
        const aOrder = a.sort_order != null ? a.sort_order : 0;
        const bOrder = b.sort_order != null ? b.sort_order : 0;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return String(a.name || '').localeCompare(String(b.name || ''));
      },
      buildHierarchy(items, parentId = null) {
        if (!Array.isArray(items)) {
          return [];
        }

        const normalizedParent = this.normalizeParentId(parentId);
        return items
          .filter(item => this.normalizeParentId(item.parent_id) === normalizedParent)
          .sort(this.compareCategories)
          .map(item => {
            return Object.assign({}, item, {
              children: this.buildHierarchy(items, item.id)
            });
          });
      },
      getSubCategories (category) {
        if (!this.categories || !Array.isArray(this.categories)) {
          return [];
        }
        return this.categories.filter(subCategory => subCategory.parent_id === category.id);
      },
      newCategory: function () {
        this.category = {
          name: '',
          description: '',
          type: 'general',
          parent_id: 0,
          is_active: true
        };
        this.modalVisible = true;
        this.$forceUpdate();
      },
      viewCategory: function (category) {
        if (this.categoryDragMoved) {
          this.categoryDragMoved = false;
          return;
        }

        if (!this.$store.getters['user/isCatalogEditor']) {
          return;
        }

        this.loading = true;
        axios.get('/api/category/' + category.id)
          .then(response => {
            this.category = response.data.category;
            this.modalVisible = true;
          })
          .catch(error => {
            console.error('Error fetching category:', error);
          })
          .finally(() => {
            this.loading = false;
          });
      },
      getCategories: function () {
        this.loading = true;

        const params = {};
        if (this.filterType) params.type = this.filterType;
        if (this.searchText) params.search = this.searchText;

        axios.get('/api/category', { params })
          .then(response => {
            if (response.data && response.data.categories && response.data.flat_categories) {
              this.categories = response.data.categories;
              this.flatCategories = response.data.flat_categories;
              store.commit('category/setCategories', response.data.categories);
              store.commit('category/setAll', response.data.flat_categories);
            } else {
              this.categories = [];
              this.flatCategories = [];
            }
          })
          .catch(error => {
            console.error('Error fetching categories:', error);
            this.categories = [];
            this.flatCategories = [];
          })
          .finally(() => {
            this.loading = false;
          });
      },
      search: function () {
        if (this.searchText.length >= 2 || this.searchText.length === 0) {
          this.getCategories();
        }
      },
      filterByType: function (type) {
        this.filterType = type;
        this.getCategories();
      },
      canDropCategoryAtIndex: function (index) {
        if (this.draggedIndex === null || this.draggedIndex === index) {
          return false;
        }
        const rows = this.displayRows;
        const fromRow = rows[this.draggedIndex];
        const toRow = rows[index];
        return !!(fromRow && toRow && fromRow.parentId === toRow.parentId);
      },
      onCategoryDragStart: function (event, index) {
        if (!this.canReorderCategories) {
          event.preventDefault();
          return;
        }
        this.categoryDragMoved = false;
        this.draggedIndex = index;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(index));
      },
      onCategoryDragOver: function (event, index) {
        if (!this.canReorderCategories) {
          return;
        }
        event.preventDefault();
        if (this.canDropCategoryAtIndex(index)) {
          this.dragOverIndex = index;
        }
      },
      onCategoryDragLeave: function (event, index) {
        if (this.dragOverIndex === index) {
          this.dragOverIndex = null;
        }
      },
      onCategoryDrop: function (event, index) {
        if (!this.canReorderCategories) {
          return;
        }
        event.preventDefault();
        if (this.canDropCategoryAtIndex(index)) {
          this.reorderDisplayRow(this.draggedIndex, index);
          this.categoryDragMoved = true;
        }
        this.dragOverIndex = null;
      },
      onCategoryDragEnd: function () {
        this.draggedIndex = null;
        this.dragOverIndex = null;
      },
      reorderDisplayRow: function (fromIndex, toIndex) {
        const rows = this.displayRows;
        const fromRow = rows[fromIndex];
        const toRow = rows[toIndex];
        if (!fromRow || !toRow || fromRow.parentId !== toRow.parentId) {
          return;
        }

        const parentId = fromRow.parentId;
        const siblings = this.flatCategories
          .filter((cat) => this.normalizeParentId(cat.parent_id) === parentId)
          .sort(this.compareCategories);

        const fromSiblingIndex = siblings.findIndex((cat) => cat.id === fromRow.category.id);
        const toSiblingIndex = siblings.findIndex((cat) => cat.id === toRow.category.id);
        if (fromSiblingIndex < 0 || toSiblingIndex < 0 || fromSiblingIndex === toSiblingIndex) {
          return;
        }

        const moved = siblings.splice(fromSiblingIndex, 1)[0];
        siblings.splice(toSiblingIndex, 0, moved);
        siblings.forEach((cat, index) => {
          cat.sort_order = index;
        });

        this.categories = this.buildHierarchy(this.flatCategories);
        store.commit('category/setCategories', this.categories);
        store.commit('category/setAll', this.flatCategories);
        this.persistCategoryOrder(parentId, siblings.map((cat) => cat.id));
      },
      persistCategoryOrder: function (parentId, categoryIds) {
        this.savingOrder = true;
        axios.put('/api/category/reorder', {
          parent_id: parentId,
          category_ids: categoryIds
        }).catch((error) => {
          console.error('Error saving category order:', error);
          this.getCategories();
        }).finally(() => {
          this.savingOrder = false;
        });
      },
      rowNumberLabel: function (row, index) {
        if (row.depth === 0) {
          const topLevelIndex = this.displayRows
            .slice(0, index + 1)
            .filter((item) => item.depth === 0).length;
          return String(topLevelIndex);
        }
        if (row.depth === 1) {
          const siblingIndex = this.displayRows
            .slice(0, index + 1)
            .filter((item) => item.depth === 1 && item.parentId === row.parentId).length;
          return row.category.id + '.' + siblingIndex;
        }
        const siblingIndex = this.displayRows
          .slice(0, index + 1)
          .filter((item) => item.depth === 2 && item.parentId === row.parentId).length;
        return row.category.id + '.' + siblingIndex;
      }
    },
    created: function () {
      auth.check(this);
      this.getCategories();
    }
  }
</script>

<style scoped>
.search-filters {
  margin-bottom: 20px;
}

.filter-row {
  display: flex;
  gap: 15px;
  align-items: end;
  margin-bottom: 15px;
}

.filter-row .field {
  flex: 1;
}

.category-row {
  background-color: #ffffff !important;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}

.category-row.clickable {
  cursor: grab;
  user-select: none;
}

.category-row.clickable:active {
  cursor: grabbing;
}

.category-row.clickable:hover {
  background-color: #ffffff !important;
}

.category-row:not(.clickable) {
  cursor: default;
}

.category-row--dragging {
  opacity: 0.45;
  cursor: grabbing !important;
}

.category-row--drag-over td {
  border-top: 2px solid #4da1ff !important;
  box-shadow: inset 0 2px 0 0 #4da1ff;
}

.sub-category {
  background-color: #ffffff !important;
}

.sub-sub-category {
  background-color: #ffffff !important;
}

.sub-category td:first-child,
.sub-sub-category td:first-child {
  padding-left: 2rem;
}

.sub-sub-category td:first-child {
  padding-left: 3rem;
}

.ui.text.muted {
  color: #323C47;
}

.ui.text.muted small {
  font-size: 0.875em;
  color: #707070;
}

.category-row td {
  background-color: #ffffff !important;
  color: #323C47 !important;
}

.ui.table tbody tr.category-row:hover {
  background-color: #ffffff !important;
}

.ui.table tbody tr.category-row:hover td {
  background-color: #ffffff !important;
}
</style>
